/**
 * Case-insensitive team-id equality — the frontend fix for EPIC-36 (the
 * identifier case seam, PMA-COCK-* — see cockpit-contract.md).
 *
 * The canonical on-disk `team_id` is the slug-plus-lowercase-hex-suffix form
 * `generate_team_id` emits and the `teams`/`matches` collections store. But
 * `GET /api/v1/logger/matches/{id}` forces it uppercase on every read
 * (`matches_new.py::_sanitize_identifier`, satisfying a Pydantic response
 * pattern, not a storage decision), while `POST`/`GET /api/v1/events/` never
 * sanitize `team_id` at all — an event's team id round-trips exactly as
 * written. So a match's `home_team.id`/`away_team.id` (as seen by the
 * frontend, via `normalizeTeamFromApi`) is always the uppercased form, and
 * an event's `team_id` may be either form depending on which path wrote it.
 * Historical documents cannot be rewritten and production is not being
 * migrated, so every comparison of an event's team id against a match's
 * team id must normalize both sides at the comparison point instead of
 * assuming either one is already canonical.
 *
 * This mirrors three independent backend defenses that already do exactly
 * this (`advanced_metrics.py`'s `_normalize_id`, `ineffective_aggregates.py`'s
 * own `_normalize_id`, `matches_new.py::validate_substitution`'s
 * `_sanitize_identifier` on both sides) and a fourth, frontend-native one:
 * `PublicMatchAnalytics.tsx`'s `normalizeComparableId`, which solves the same
 * problem by normalizing once at the data-ingestion boundary rather than at
 * each comparison. The cockpit's `match.home_team.id` has no such boundary
 * available (`normalizeTeamFromApi` only ever sees the already-uppercased
 * `team_id` the API hands it — there is no raw-case path left to intercept),
 * so the fix here is comparison-point normalization: one helper, used at
 * every site that compares an event's `team_id` against a match's team id.
 */
export const sameTeamId = (
  a: string | null | undefined,
  b: string | null | undefined,
): boolean => {
  const left = (a ?? "").trim().toLowerCase();
  const right = (b ?? "").trim().toLowerCase();
  // Two absent/blank ids are not "the same team" — an empty comparand means
  // "no team resolved," not a wildcard match, so it must never coincide with
  // another empty comparand (or with itself).
  if (!left || !right) return false;
  return left === right;
};
